"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useAuthContext } from "@/context/authContext";
import { openAuthenticatedPriceMonitor } from "@/hooks/usePriceMonitorRequest";
import { useNativeSignIn } from "@/hooks/useNativeSignIn";
import {
  isPendingPriceMonitorRequestExpired,
  usePriceMonitorRequestStore,
} from "@/stores/priceMonitorRequestStore";
import { useUiStore } from "@/stores/uiStore";

export default function PriceMonitorAuthResume() {
  const { isLoaded, isSignedIn } = useAuthContext();
  const { openSignIn } = useNativeSignIn();
  const pathname = usePathname();
  const pendingRequest = usePriceMonitorRequestStore((state) => state.pendingRequest);
  const itemModalOpen = useUiStore((state) => state.itemModalOpen);
  const favoritesModalOpen = useUiStore((state) => state.favoritesModalOpen);

  useEffect(() => {
    if (!pendingRequest) return;

    const remainingMs = pendingRequest.expiresAt - Date.now();
    if (remainingMs <= 0) {
      usePriceMonitorRequestStore.getState().actions.clear(pendingRequest.requestToken);
      return;
    }

    const expiryTimer = window.setTimeout(() => {
      usePriceMonitorRequestStore.getState().actions.clear(pendingRequest.requestToken);
    }, remainingMs);

    return () => window.clearTimeout(expiryTimer);
  }, [pendingRequest]);

  useEffect(() => {
    if (!pendingRequest) return;

    const sourceModalAbandoned =
      pendingRequest.sourceModalOpen &&
      ((pendingRequest.options?.source === "item" && !itemModalOpen) ||
        (pendingRequest.options?.source === "favorites" && !favoritesModalOpen));

    if (
      isPendingPriceMonitorRequestExpired(pendingRequest) ||
      pendingRequest.pathname !== pathname ||
      sourceModalAbandoned
    ) {
      usePriceMonitorRequestStore.getState().actions.clear(pendingRequest.requestToken);
      return;
    }

    if (!isLoaded) return;

    const requestState = usePriceMonitorRequestStore.getState();

    if (!isSignedIn) {
      if (!requestState.signInStarted) {
        requestState.actions.markSignInStarted(pendingRequest.requestToken);
        void openSignIn().catch(() => requestState.actions.clear(pendingRequest.requestToken));
      }
      return;
    }

    const request = requestState.actions.takePending(pendingRequest.requestToken);
    if (request) {
      openAuthenticatedPriceMonitor(request);
    }
  }, [
    favoritesModalOpen,
    isLoaded,
    isSignedIn,
    itemModalOpen,
    openSignIn,
    pathname,
    pendingRequest,
  ]);

  return null;
}
